import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Fab,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import request from "../../utilities/requests";
import EntryCard from "./components/EntryCard";
import LogWorkDialog from "./components/LogWorkDialog";

export default function WorkLog() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterWeekOf, setFilterWeekOf] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");

  useEffect(() => {
    fetchSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSubmissions = async () => {
    setLoading(true);
    const params = [];
    if (filterWeekOf)
      params.push(`weekOf=${dayjs(filterWeekOf).format("YYYY-MM-DD")}`);
    if (filterStatus) params.push(`status=${filterStatus}`);
    const query =
      "worklog/mine" + (params.length ? "?" + params.join("&") : "");
    const result = await request(query);
    setLoading(false);
    if (result?.success) setSubmissions(result.entries || []);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, pb: 10, maxWidth: 680, mx: "auto" }}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        Work Log
      </Typography>

      {/* Filters */}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ display: "flex", gap: 1.5, mb: 2.5, flexWrap: "wrap" }}>
          <DatePicker
            label="Week of"
            value={filterWeekOf}
            onChange={setFilterWeekOf}
            slotProps={{
              textField: {
                size: "small",
                sx: { flex: "1 1 160px", minWidth: 140 },
              },
            }}
          />
          <FormControl size="small" sx={{ flex: "1 1 130px", minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              label="Status"
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="flagged">Flagged</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            onClick={fetchSubmissions}
            sx={{ flexShrink: 0 }}
          >
            Search
          </Button>
        </Box>
      </LocalizationProvider>

      <Divider sx={{ mb: 2.5 }} />

      {/* Entries list */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : submissions.length === 0 ? (
        <Box sx={{ textAlign: "center", mt: 6 }}>
          <Typography color="text.secondary" variant="body2">
            No submissions yet.
          </Typography>
          <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
            Tap the button below to log your first entry.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {submissions.map((entry) => (
            <EntryCard key={entry._id} entry={entry} />
          ))}
        </Box>
      )}

      {/* Log Work FAB */}
      <Fab
        variant="extended"
        color="primary"
        onClick={() => setDialogOpen(true)}
        sx={{
          position: "fixed",
          bottom: { xs: 24, sm: 32 },
          right: { xs: 24, sm: 32 },
          gap: 1,
        }}
      >
        <AddIcon />
        Log Work
      </Fab>

      <LogWorkDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmitted={fetchSubmissions}
      />
    </Box>
  );
}
